import React, { Component } from "react";
import { withApollo } from "react-apollo";
import * as Cookies from "es-cookie";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import Select from "react-select";
import MultiSelect from "react-multi-select-component";
import gql from "graphql-tag";

import { CommSubTabs } from "../CommSubTabs";
import MessagesPanel from "../panels/MessagesPanel";
import TemplatesPanel from "../panels/TemplatesPanel";
import { SMS_STATS, SMS_MESSAGES, SMS_TEMPLATES } from "../sampleData";
import { GET_SMS_TEMPLATES, SEND_SMS_MUTATION } from "../../../utils/smsHelper";
import { attachModuleGuard, AccessDeniedModal } from "../../../../utils/moduleGuard";
import { scopeBranches } from "../../../../utils/branchScope";
import "../_communicationManagement.scss";

// This tab is the "communication_management" module. Its grant row has an empty
// sub_module_name, so it's checked by module_key only (no sub-module).
const COMMUNICATION_MODULE = ["communication_management"];

const ACCENT = "#2563eb";
const SUB_TABS = [
  { key: "messages", label: "Messages", icon: "fal fa-comment-alt" },
  { key: "templates", label: "Templates", icon: "fal fa-file-alt" },
];

const GET_BRANCHES = gql`
  query getBranches($institution_id: Int!) {
    getBranches(institution_id: $institution_id) {
      id
      branch_name
    }
  }
`;

const GET_STUDENT_DETAILS = gql`
  query getCombinedAcademicAndNonAcademicStudents($institution_id: Int!, $branch_id: String) {
    getCombinedAcademicAndNonAcademicStudents(institution_id: $institution_id, branch_id: $branch_id) {
      student_id: edu_student_id
      roll_no
      student_name
      class_id
      class_name
      branch_id
      branch_name
      section_id
      section_name
      student_mobile
      parent_mobile
    }
  }
`;

const GET_SENT_SMS_LIST = gql`
  query($institute_id: Int!) {
    getSentSMSList(institute_id: $institute_id) {
      id
      recipient
      phone
      audience
      message
      status
      sentOn
    }
  }
`;

class SMSTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSubTab: "messages",
      templates: [],
      sentHistory: [],
      loading: false,

      // Modal state
      showSendModal: false,
      loadingData: false,
      sendingSMS: false,

      // Recipient selection options (derived dynamically)
      branchesOptions: [],
      classesOptions: [],
      sectionsOptions: [],
      filteredStudents: [],
      selectedStudents: [], // IDs of selected students

      // Selection state
      selectedBranch: null,
      selectedClass: [],
      selectedSection: [],
      selectedTemplate: null,
      messageText: "",

      studentsData: [], // raw list of all students for local filtering

      // Dynamic template variable inputs and character counter state
      templateVars: [],
      charCount: 0,
      smsParts: 1,

      // Flipped to true by the guard when a role without is_edit tries to send.
      accessDeniedShow: false
    };

    // "New SMS" / "Broadcast SMS" are writes. The button stays VISIBLE; the guard
    // intercepts and opens the Access Denied modal when the role lacks is_edit.
    this.guard = attachModuleGuard(this, COMMUNICATION_MODULE);
  }

  componentDidMount() {
    this.fetchTemplates();
    this.fetchSentHistory();
  }

  fetchTemplates = async () => {
    const { client } = this.props;
    const instituteId = Cookies.get("institutionid");
    const branchId = Cookies.get("branchid");

    if (!instituteId) return;

    try {
      const { data } = await client.query({
        query: GET_SMS_TEMPLATES,
        variables: {
          institute_id: parseInt(instituteId),
          branch_id: branchId ? parseInt(branchId) : null
        },
        fetchPolicy: "no-cache"
      });

      const backendTemplates = data?.getSMSTemplates || [];
      if (backendTemplates.length > 0) {
        const mapped = backendTemplates.map((t, idx) => ({
          id: t.template_id || `backend_${idx}`,
          name: (t.template_name && t.template_name.trim()) || `Template ${t.template_id || idx}`,
          category: "Transactional",
          status: "Approved",
          body: t.template_text || "",
          raw: t
        }));
        this.setState({ templates: mapped });
      } else {
        this.setState({ templates: [] });
      }
    } catch (err) {
      console.error("Error fetching SMS templates:", err);
      this.setState({ templates: [] });
    }
  };

  fetchSentHistory = async () => {
    const { client } = this.props;
    const instituteId = Cookies.get("institutionid");
    if (!instituteId) return;

    this.setState({ loading: true });
    try {
      const { data } = await client.query({
        query: GET_SENT_SMS_LIST,
        variables: { institute_id: parseInt(instituteId) },
        fetchPolicy: "network-only"
      });

      const list = data?.getSentSMSList || [];
      if (list.length > 0) {
        this.setState({ sentHistory: list, loading: false });
      } else {
        this.setState({ sentHistory: SMS_MESSAGES, loading: false });
      }
    } catch (err) {
      console.error("Error fetching sent SMS history:", err);
      this.setState({ sentHistory: SMS_MESSAGES, loading: false });
    }
  };

  handleNewMessageClick = async () => {
    // is_edit gate — opens Access Denied and stops before the broadcast modal appears.
    if (!this.guard.requireEdit()) return;
    this.setState({ showSendModal: true, loadingData: true });
    try {
      const instituteId = parseInt(Cookies.get("institutionid"));

      // 1. Fetch branches list first
      const branchesRes = await this.props.client.query({
        query: GET_BRANCHES,
        variables: { institution_id: instituteId },
        fetchPolicy: "network-only"
      });

      // DATA-ENTRY: this Branch select stays on the form — its value is written as
      // branch_id on the SMS broadcast. The header (NavbarOne) only narrows which
      // branches may be picked.
      const branches = scopeBranches(branchesRes.data?.getBranches || []);
      const branchesOptions = branches.map(b => ({
        value: b.id,
        label: b.branch_name
      }));

      let selectedBranch = null;
      let studentsData = [];

      // 2. Default to the first branch if available, and fetch its students
      if (branchesOptions.length > 0) {
        selectedBranch = branchesOptions[0];
        const studentsRes = await this.props.client.query({
          query: GET_STUDENT_DETAILS,
          variables: { institution_id: instituteId, branch_id: String(selectedBranch.value) },
          fetchPolicy: "network-only"
        });
        studentsData = studentsRes.data?.getCombinedAcademicAndNonAcademicStudents || [];
      }

      this.setState({
        studentsData,
        branchesOptions,
        selectedBranch,
        loadingData: false
      }, () => {
        if (selectedBranch) {
          // Compute classes options from newly fetched students
          const classesOptions = [];
          const seenClasses = new Set();
          studentsData.forEach(s => {
            if (s.class_id && !seenClasses.has(s.class_id)) {
              seenClasses.add(s.class_id);
              classesOptions.push({
                value: s.class_id,
                label: s.class_name || `Class ${s.class_id}`
              });
            }
          });
          this.setState({
            classesOptions,
            selectedClass: [],
            selectedSection: [],
            sectionsOptions: [],
            filteredStudents: [],
            selectedStudents: []
          });
        }
      });
    } catch (err) {
      console.error("Error loading student recipients:", err);
      this.setState({ loadingData: false });
    }
  };

  handleBranchChange = async (selectedBranch) => {
    if (!selectedBranch) {
      this.setState({
        selectedBranch: null,
        selectedClass: [],
        selectedSection: [],
        classesOptions: [],
        sectionsOptions: [],
        filteredStudents: [],
        selectedStudents: []
      });
      return;
    }

    this.setState({ loadingData: true, selectedBranch });
    try {
      const instituteId = parseInt(Cookies.get("institutionid"));
      const studentsRes = await this.props.client.query({
        query: GET_STUDENT_DETAILS,
        variables: { institution_id: instituteId, branch_id: String(selectedBranch.value) },
        fetchPolicy: "network-only"
      });
      const studentsData = studentsRes.data?.getCombinedAcademicAndNonAcademicStudents || [];

      // Compute classes options from newly fetched students
      const classesOptions = [];
      const seenClasses = new Set();
      studentsData.forEach(s => {
        if (s.class_id && !seenClasses.has(s.class_id)) {
          seenClasses.add(s.class_id);
          classesOptions.push({
            value: s.class_id,
            label: s.class_name || `Class ${s.class_id}`
          });
        }
      });

      this.setState({
        studentsData,
        classesOptions,
        selectedClass: [],
        selectedSection: [],
        sectionsOptions: [],
        filteredStudents: [],
        selectedStudents: [],
        loadingData: false
      });
    } catch (err) {
      console.error("Error fetching students on branch change:", err);
      this.setState({ loadingData: false });
    }
  };

  handleClassChange = (selectedClasses) => {
    const classes = selectedClasses || [];
    const classIds = classes.map(c => Number(c.value));

    if (classIds.length === 0) {
      this.setState({
        selectedClass: [],
        selectedSection: [],
        sectionsOptions: [],
        filteredStudents: [],
        selectedStudents: []
      });
      return;
    }

    // Filter students matching the selected classes
    const classStudents = this.state.studentsData.filter(
      s => classIds.includes(Number(s.class_id))
    );

    const sectionsOptions = [];
    const seenSections = new Set();
    classStudents.forEach(s => {
      if (s.section_id && !seenSections.has(s.section_id)) {
        seenSections.add(s.section_id);
        sectionsOptions.push({
          value: s.section_id,
          label: s.section_name || `Section ${s.section_id}`
        });
      }
    });

    this.setState({
      selectedClass: classes,
      selectedSection: [],
      sectionsOptions,
      filteredStudents: [],
      selectedStudents: []
    });
  };

  handleSectionChange = (selectedSections) => {
    const sections = selectedSections || [];
    const sectionIds = sections.map(s => Number(s.value));
    const classIds = (this.state.selectedClass || []).map(c => Number(c.value));

    if (sectionIds.length === 0 || classIds.length === 0) {
      this.setState({
        selectedSection: sections,
        filteredStudents: [],
        selectedStudents: []
      });
      return;
    }

    const filteredStudents = this.state.studentsData.filter(s =>
      classIds.includes(Number(s.class_id)) &&
      sectionIds.includes(Number(s.section_id))
    );

    this.setState({
      selectedSection: sections,
      filteredStudents,
      selectedStudents: filteredStudents.map(s => s.student_id)
    });
  };

  handleStudentCheck = (studentId) => {
    const { selectedStudents } = this.state;
    if (selectedStudents.includes(studentId)) {
      this.setState({ selectedStudents: selectedStudents.filter(id => id !== studentId) });
    } else {
      this.setState({ selectedStudents: [...selectedStudents, studentId] });
    }
  };

  handleSelectAllToggle = () => {
    const { filteredStudents, selectedStudents } = this.state;
    const filteredIds = filteredStudents.map(s => s.student_id);
    const allSelected = filteredIds.every(id => selectedStudents.includes(id));

    if (allSelected) {
      this.setState({ selectedStudents: selectedStudents.filter(id => !filteredIds.includes(id)) });
    } else {
      this.setState({ selectedStudents: [...new Set([...selectedStudents, ...filteredIds])] });
    }
  };

  parseTemplateVars = (bodyText) => {
    const matches = bodyText.match(/\{#var#\}/g) || [];
    return matches.map((_, idx) => ({
      index: idx,
      placeholder: `{#var#}`,
      value: ""
    }));
  };

  getMergedMessage = () => {
    const { selectedTemplate, messageText, templateVars } = this.state;
    if (!selectedTemplate) return messageText; // Manual editor

    let merged = messageText;
    templateVars.forEach(v => {
      // Replace the first occurrence of {#var#} with the user's input value
      merged = merged.replace(`{#var#}`, v.value || `[Variable ${v.index + 1}]`);
    });
    return merged;
  };

  calculateSMSBilling = (text) => {
    const length = text.length;
    let parts = 1;
    if (length > 160) {
      parts = Math.ceil(length / 153);
    }
    return { length, parts };
  };

  updateSMSCount = () => {
    const mergedText = this.getMergedMessage();
    const billing = this.calculateSMSBilling(mergedText);
    this.setState({
      charCount: billing.length,
      smsParts: billing.parts
    });
  };

  handleVariableChange = (index, val) => {
    const { templateVars } = this.state;
    const updated = templateVars.map(v => {
      if (v.index === index) {
        return { ...v, value: val };
      }
      return v;
    });

    this.setState({ templateVars: updated }, () => {
      this.updateSMSCount();
    });
  };

  handleMessageTextChange = (e) => {
    const val = e.target.value;
    this.setState({ messageText: val }, () => {
      this.updateSMSCount();
    });
  };

  handleTemplateChange = (selectedTemplate) => {
    if (!selectedTemplate) {
      this.setState({
        selectedTemplate: null,
        messageText: "",
        templateVars: [],
        charCount: 0,
        smsParts: 1
      });
      return;
    }
    const tpl = this.state.templates.find(t => String(t.id) === String(selectedTemplate.value));
    const bodyText = tpl ? tpl.body : "";
    const vars = this.parseTemplateVars(bodyText);

    this.setState({
      selectedTemplate,
      messageText: bodyText,
      templateVars: vars
    }, () => {
      this.updateSMSCount();
    });
  };

  handleSendSMS = async () => {
    // Last gate before the mutation — the modal could already be open when a grant changes.
    if (!this.guard.requireEdit()) { this.setState({ showSendModal: false }); return; }

    const { selectedStudents, filteredStudents, selectedTemplate, selectedBranch } = this.state;
    const finalBaseMessage = this.getMergedMessage();

    if (selectedStudents.length === 0) {
      alert("Please select at least one recipient student.");
      return;
    }
    if (!finalBaseMessage.trim()) {
      alert("SMS body text cannot be empty.");
      return;
    }

    this.setState({ sendingSMS: true });
    try {
      const instituteId = parseInt(Cookies.get("institutionid"));
      const branchId = selectedBranch ? parseInt(selectedBranch.value) : 0;

      const templateDetails = selectedStudents.map(studentId => {
        const student = filteredStudents.find(s => s.student_id === studentId);
        const mobile = student ? student.student_mobile || student.parent_mobile : "";
        const personalizedMessage = finalBaseMessage.replace(/{student_name}/g, student ? student.student_name : "");

        const queryParams = new URLSearchParams({
          mobile: String(mobile).replace(/\D/g, ""),
          message: personalizedMessage,
          template_id: selectedTemplate ? String(selectedTemplate.value) : ""
        });

        return { template: queryParams.toString() };
      }).filter(item => {
        const search = new URLSearchParams(item.template);
        return search.get("mobile") !== "";
      });

      if (templateDetails.length === 0) {
        alert("None of the selected students have valid phone numbers.");
        this.setState({ sendingSMS: false });
        return;
      }

      await this.props.client.mutate({
        mutation: SEND_SMS_MUTATION,
        variables: {
          params: {
            institute_id: instituteId,
            branch_id: branchId,
            template_details: templateDetails
          }
        }
      });

      alert("SMS Broadcast queued successfully!");
      this.setState({
        showSendModal: false,
        sendingSMS: false,
        selectedBranch: null,
        selectedClass: [],
        selectedSection: [],
        selectedStudents: [],
        selectedTemplate: null,
        messageText: "",
        templateVars: [],
        charCount: 0,
        smsParts: 1
      });
      
      this.fetchSentHistory();
    } catch (err) {
      console.error("Failed to send SMS:", err);
      alert("Failed to send SMS: " + err.message);
      this.setState({ sendingSMS: false });
    }
  };

  handleSubTabClick = (key) => this.setState({ activeSubTab: key });

  renderSubContent = () => {
    const { templates, sentHistory, loading } = this.state;

    // Dynamically calculate stats based on logs
    const totalSent = sentHistory.length;
    const deliveredCount = sentHistory.filter(h => h.status === "Delivered").length;
    const failedCount = sentHistory.filter(h => h.status === "Failed").length;

    const dynamicStats = [
      { label: "Total Sent", value: totalSent.toLocaleString(), icon: "fal fa-comment-alt-lines", color: "#2563eb", sub: "All time logs" },
      { label: "Delivered", value: deliveredCount.toLocaleString(), icon: "fal fa-check-double", color: "#10b981", sub: `${totalSent > 0 ? ((deliveredCount/totalSent)*100).toFixed(1) : 0}% success` },
      { label: "Credits Left", value: "71,690", icon: "fal fa-coins", color: "#d97706", sub: "of 1,00,000" },
      { label: "Failed", value: failedCount.toLocaleString(), icon: "fal fa-times-circle", color: "#dc2626", sub: `${totalSent > 0 ? ((failedCount/totalSent)*100).toFixed(1) : 0}% failed` },
    ];

    switch (this.state.activeSubTab) {
      case "templates":
        return (
          <TemplatesPanel
            channel="SMS"
            accentColor={ACCENT}
            templates={templates}
          />
        );
      case "messages":
      default:
        return (
          <div>
            {loading && (
              <div className="d-flex justify-content-center my-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="sr-only">Loading logs...</span>
                </div>
              </div>
            )}
            <MessagesPanel
              channel="SMS"
              accentColor={ACCENT}
              stats={dynamicStats}
              messages={sentHistory}
              onNewMessage={this.handleNewMessageClick}
            />
          </div>
        );
    }
  };

  render() {
    const {
      showSendModal,
      loadingData,
      sendingSMS,
      branchesOptions,
      classesOptions,
      sectionsOptions,
      filteredStudents,
      selectedStudents,
      selectedBranch,
      selectedClass,
      selectedSection,
      selectedTemplate,
      messageText,
      templates,
      templateVars,
      charCount,
      smsParts
    } = this.state;

    const templatesDropdownOptions = templates.map(t => ({ value: t.id, label: t.name }));

    const allStudentsSelected = filteredStudents.length > 0 && 
      filteredStudents.every(s => selectedStudents.includes(s.student_id));

    return (
      <div className="comm-mgmt">
        <CommSubTabs
          tabs={SUB_TABS}
          activeKey={this.state.activeSubTab}
          onSelect={this.handleSubTabClick}
        />
        {this.renderSubContent()}

        {/* Send SMS Modal with Class and Section Selector */}
        <Modal show={showSendModal} onHide={() => this.setState({ showSendModal: false })} size="lg" centered dialogClassName="premium-sms-modal">
          <Modal.Header closeButton className="bg-primary text-white py-3">
            <Modal.Title className="h5 m-0" style={{ color: "#fff" }}>
              <i className="fal fa-paper-plane mr-2" /> New SMS Broadcast
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4" style={{ background: "#fff", minHeight: "480px" }}>
            {loadingData ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-2" role="status" />
                <p className="text-muted mb-0">Loading student recipients...</p>
              </div>
            ) : (
              <Row>
                {/* Main Form Panel */}
                <Col lg={12}>
                  <Form>
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="font-weight-bold">Select Branch</Form.Label>
                          <Select
                            options={branchesOptions}
                            value={selectedBranch}
                            onChange={this.handleBranchChange}
                            placeholder="Choose branch..."
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="font-weight-bold">Select Class</Form.Label>
                          <MultiSelect
                            overrideStrings={{ allItemsAreSelected: "All Classes selected", selectSomeItems: "Choose Class(es)..." }}
                            options={classesOptions}
                            value={selectedClass}
                            onChange={this.handleClassChange}
                            labelledBy="Class"
                            disabled={!selectedBranch}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="font-weight-bold">Select Section</Form.Label>
                          <MultiSelect
                            overrideStrings={{ allItemsAreSelected: "All Sections selected", selectSomeItems: "Choose Section(s)..." }}
                            options={sectionsOptions}
                            value={selectedSection}
                            onChange={this.handleSectionChange}
                            labelledBy="Section"
                            disabled={!selectedClass || selectedClass.length === 0}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Recipient Checklist */}
                    {selectedSection && selectedSection.length > 0 && (
                      <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Form.Label className="font-weight-bold mb-0">
                            Recipients ({selectedStudents.length} of {filteredStudents.length})
                          </Form.Label>
                          {filteredStudents.length > 0 && (
                            <Form.Check
                              type="checkbox"
                              id="select-all-students"
                              label="Select All"
                              checked={allStudentsSelected}
                              onChange={this.handleSelectAllToggle}
                              style={{ fontWeight: 600 }}
                            />
                          )}
                        </div>
                        <div 
                          className="border rounded p-3" 
                          style={{ maxHeight: "160px", overflowY: "auto", background: "#f8fafc" }}
                        >
                          {filteredStudents.length > 0 ? (
                            filteredStudents.map(student => (
                              <Form.Check
                                key={student.student_id}
                                type="checkbox"
                                id={`student-check-${student.student_id}`}
                                label={`${student.roll_no ? `${student.roll_no} - ` : ""}${student.student_name} (${student.student_mobile || student.parent_mobile || "No Mobile"})`}
                                checked={selectedStudents.includes(student.student_id)}
                                onChange={() => this.handleStudentCheck(student.student_id)}
                                className="mb-2 recipient-checkbox"
                                disabled={!student.student_mobile && !student.parent_mobile}
                              />
                            ))
                          ) : (
                            <div className="text-center text-muted py-3">No students found in this section.</div>
                          )}
                        </div>
                      </Form.Group>
                    )}

                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="font-weight-bold">Select Template</Form.Label>
                          <Select
                            options={templatesDropdownOptions}
                            value={selectedTemplate}
                            onChange={this.handleTemplateChange}
                            placeholder="Choose SMS template..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Template Variable Fields */}
                    {selectedTemplate && templateVars.length > 0 && (
                      <div className="bg-light p-3 rounded mb-3 border border-primary-light">
                        <h6 className="font-weight-bold text-primary mb-2" style={{ fontSize: "14px" }}>
                          <i className="fal fa-sliders-h mr-2" /> Template Variables ({templateVars.length})
                        </h6>
                        <Row>
                          {templateVars.map((v, i) => (
                            <Col md={6} key={v.index} className="mb-2">
                              <Form.Group className="mb-0">
                                <Form.Label className="small font-weight-bold mb-1">Variable {i + 1} ({`{#var#}`})</Form.Label>
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  value={v.value}
                                  onChange={(e) => this.handleVariableChange(v.index, e.target.value)}
                                  placeholder={`Enter value for var ${i + 1}...`}
                                />
                              </Form.Group>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}

                    <Form.Group className="mb-0">
                      <Form.Label className="font-weight-bold">Message Content</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={this.getMergedMessage()}
                        onChange={this.handleMessageTextChange}
                        placeholder="Message content will be loaded automatically once you select a template."
                        disabled={true} // Always locked to ensure DLT compliance
                        className="bg-light text-muted"
                      />
                      <Form.Text className="text-info font-weight-bold">
                        <i className="fal fa-info-circle mr-1" /> Template text is locked to ensure DLT compliance. Use the variable input fields above to customize.
                      </Form.Text>
                    </Form.Group>

                    {/* Character & SMS Part Calculator */}
                    <div className="mt-3">
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
                        <span className="badge badge-pill badge-primary px-3 py-2" style={{ fontSize: "12px", backgroundColor: "#2563eb", color: "#fff" }}>
                          <strong>{charCount}</strong> characters
                        </span>
                        <span className="badge badge-pill badge-info px-3 py-2" style={{ fontSize: "12px", backgroundColor: "#0ea5e9", color: "#fff" }}>
                          <strong>{smsParts}</strong> SMS Part{smsParts > 1 ? "s" : ""}
                        </span>
                        {selectedStudents.length > 0 && (
                          <span className="badge badge-pill badge-success px-3 py-2" style={{ fontSize: "12px", backgroundColor: "#10b981", color: "#fff" }}>
                            <strong>{smsParts * selectedStudents.length}</strong> credits total
                          </span>
                        )}
                      </div>
                      <small className="text-muted d-block mt-2">
                        1 part = 160 characters. Multi-part uses 153 characters/part.
                      </small>
                    </div>
                  </Form>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => this.setState({ showSendModal: false })} disabled={sendingSMS}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={this.handleSendSMS} 
              disabled={sendingSMS || loadingData || !selectedSection || selectedSection.length === 0 || filteredStudents.length === 0 || !selectedTemplate}
            >
              {sendingSMS ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2" /> Sending...
                </>
              ) : (
                <>
                  <i className="fal fa-paper-plane mr-2" /> Broadcast SMS
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Shown when a role without is_edit clicks New SMS / Broadcast SMS. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />
      </div>
    );
  }
}

export default withApollo(SMSTab);
